import {
  deleteUpsellGroupsGroupId,
  getUpsellGroups,
  getUpsellItems,
  patchUpsellGroupsGroupId,
  postUpsellCopy,
  postUpsellGroups,
  putUpsellGroupsOrder,
  putUpsellItemsAddonProductIdLinks,
  putUpsellItemsOrder,
} from '@agos/api-client/upsell'
import type { QueryClient } from '@tanstack/react-query'

/**
 * The upsell screen's requests - `FRONTEND.md` §9.711 on `BACKEND.md` §10A.365.
 *
 * **`customFetch` rather than a generated hook, and that is §9.509's lesson.** The endpoints were written by a
 * parallel backend lane and are not in the committed spec yet, so no hook exists. Every path is on
 * `typed-fetches.test.ts`'s `AWAITING_GENERATED_CLIENT` list, which **fails the day the client is regenerated**
 * and says to move these calls to the hooks; `src/generated` is not touched.
 *
 * **Every response is read as `unknown` and parsed here**, rather than asserted into a declared shape. §3.122's
 * defect was a reader taking a key the server does not send, which read every franchise as active for a month
 * because `undefined !== false`; a parser that drops a row it cannot read says so in one place instead.
 *
 * **The query keys are the paths**, which is orval's own convention (`['/api/v1/products']`), so the day the
 * generated hooks replace these the cache entries line up and no invalidation needs rewriting.
 */

export const UPSELL_GROUPS_PATH = '/api/v1/upsell/groups'
export const UPSELL_ITEMS_PATH = '/api/v1/upsell/items'

export const UPSELL_GROUPS_KEY = [UPSELL_GROUPS_PATH] as const
export const UPSELL_ITEMS_KEY = [UPSELL_ITEMS_PATH] as const

/** One group as `GET /upsell/groups` answers it. */
export interface UpsellGroup {
  id: number
  name: string
  /** Null is *no limit*; otherwise 1 or more, counted **per unit** of the product (#133). */
  maxPerUnit: number | null
  /** Null is *each item at its own price*; 0 is free (#134). */
  priceCents: number | null
  sortOrder: number
  active: boolean
}

/** One product this add-on is offered with, and the group it is offered under (null: ungrouped). */
export interface UpsellLink {
  productId: number
  groupId: number | null
}

/** One add-on as `GET /upsell/items` answers it - the reverse view of `product_addons` (§10A.364). */
export interface UpsellItem {
  addonProductId: number
  /** Null when the add-on has no `upsell_items` row yet: it sorts after the ordered ones, by name. */
  sortOrder: number | null
  links: UpsellLink[]
}

/** What `POST` and `PATCH /upsell/groups` take. */
export interface UpsellGroupPayload {
  name: string
  maxPerUnit: number | null
  priceCents: number | null
  active: boolean
}

/** What `PUT /upsell/items/{addonProductId}/links` takes. */
export interface UpsellLinksPayload {
  groupId: number | null
  productIds: number[]
}

export type CopyMode = 'merge' | 'replace'

/** What `POST /upsell/copy` takes. */
export interface UpsellCopyPayload {
  fromProductId: number
  toProductIds: number[]
  mode: CopyMode
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function integer(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null
}

/**
 * The groups, or `[]` for a payload that is not a list.
 *
 * **A row with no id or no name is dropped rather than drawn**: an editor over a group it cannot address would
 * send a `PATCH` to `/upsell/groups/undefined`. A limit below one and a negative price are read as *none*, which
 * is what the server refuses to store in the first place.
 */
export function upsellGroupsOf(payload: unknown): UpsellGroup[] {
  if (!Array.isArray(payload)) return []
  const groups: UpsellGroup[] = []
  for (const row of payload) {
    if (!isRecord(row)) continue
    const id = integer(row.id)
    if (id === null || typeof row.name !== 'string') continue
    const maxPerUnit = integer(row.maxPerUnit)
    const priceCents = integer(row.priceCents)
    groups.push({
      id,
      name: row.name,
      maxPerUnit: maxPerUnit !== null && maxPerUnit >= 1 ? maxPerUnit : null,
      priceCents: priceCents !== null && priceCents >= 0 ? priceCents : null,
      sortOrder: integer(row.sortOrder) ?? 0,
      // Absent is not false: a server that stops sending the flag must not read every group as switched off.
      active: row.active !== false,
    })
  }
  return groups
}

/** The links of one item, each a product and the group (or none) it is offered under. */
function linksOf(value: unknown): UpsellLink[] {
  if (!Array.isArray(value)) return []
  const links: UpsellLink[] = []
  for (const row of value) {
    if (!isRecord(row)) continue
    const productId = integer(row.productId)
    if (productId === null) continue
    links.push({ productId, groupId: integer(row.groupId) })
  }
  return links
}

/** The items, or `[]` for a payload that is not a list. A row with no add-on id is dropped. */
export function upsellItemsOf(payload: unknown): UpsellItem[] {
  if (!Array.isArray(payload)) return []
  const items: UpsellItem[] = []
  for (const row of payload) {
    if (!isRecord(row)) continue
    const addonProductId = integer(row.addonProductId)
    if (addonProductId === null) continue
    items.push({ addonProductId, sortOrder: integer(row.sortOrder), links: linksOf(row.links) })
  }
  return items
}

/* The calls go through the generated client since §10A.365 was regenerated into it (the merge of lanes U1 and U2); the
   answers still go through this file's own `...Of(unknown)` parsers, which drop a row the screen could not address. */

export async function fetchUpsellGroups(): Promise<UpsellGroup[]> {
  return upsellGroupsOf(await getUpsellGroups())
}

export async function fetchUpsellItems(): Promise<UpsellItem[]> {
  return upsellItemsOf(await getUpsellItems())
}

export async function createUpsellGroup(body: UpsellGroupPayload): Promise<unknown> {
  return postUpsellGroups(body)
}

export async function updateUpsellGroup(groupId: number, body: UpsellGroupPayload): Promise<unknown> {
  return patchUpsellGroupsGroupId(groupId, body)
}

/** Detaches the group's links rather than deleting them - they become ungrouped (§10A.365). */
export async function deleteUpsellGroup(groupId: number): Promise<unknown> {
  return deleteUpsellGroupsGroupId(groupId)
}

export async function saveUpsellGroupOrder(groupIds: readonly number[]): Promise<unknown> {
  return putUpsellGroupsOrder({ groupIds: [...groupIds] })
}

/** Replaces this add-on's links **in that group** (or the ungrouped ones) with exactly these products. */
export async function saveUpsellLinks(
  addonProductId: number,
  body: UpsellLinksPayload,
): Promise<unknown> {
  return putUpsellItemsAddonProductIdLinks(addonProductId, body)
}

export async function saveUpsellItemOrder(addonProductIds: readonly number[]): Promise<unknown> {
  return putUpsellItemsOrder({ addonProductIds: [...addonProductIds] })
}

export async function copyUpsells(body: UpsellCopyPayload): Promise<unknown> {
  return postUpsellCopy(body)
}

/**
 * Whether a cached query is one product's add-on read - `['/api/v1/products/{id}/addons']`.
 *
 * The product dialog seeds *Doplnky k produktu* from that read, so a save here that moves a link has to make the
 * dialog read it again, or it reopens on what the product was offered before this screen changed it.
 */
export function isProductAddonsKey(queryKey: readonly unknown[]): boolean {
  const first = queryKey[0]
  return typeof first === 'string' && /^\/api\/v1\/products\/\d+\/addons$/.test(first)
}

/**
 * After any write on this screen: the two upsell reads, and every product's add-on read.
 *
 * Awaited by the caller, so an optimistic order is let go only once the server's order has arrived - letting it
 * go first would draw the old order for the length of a refetch and look like the drop had not taken.
 */
export async function refreshUpsellReads(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: UPSELL_ITEMS_KEY }),
    queryClient.invalidateQueries({ queryKey: UPSELL_GROUPS_KEY }),
    queryClient.invalidateQueries({ predicate: (query) => isProductAddonsKey(query.queryKey) }),
  ])
}
